import React from 'react';
import { styled } from '@mui/material/styles';
import { APICONSTANTS, CONSTANTS } from '../Constants';
import { Grid } from '@mui/material';
import PedigreeBox from './PedigreeBox';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'src/axios';
import { EVENTS } from 'src/components/admin/appReducer';
import { usePedigreeLock } from 'src/hooks/usePedigreeLock';
import { usePersonLocks } from 'src/hooks/usePersonLocks';
import { useQueryClient } from '@tanstack/react-query';
import { usePedigreeInstance } from './pedigree-tool/pedigree/instanceContext';
import { useWorkspaceActions } from 'src/store/WorkspaceContext';
import {
  shouldFetchFamily,
  familyLoadFailureEffects
} from './workspace/familyLoadPolicy';
import { shouldSignalEdit } from './workspace/editSignalPolicy';
import { useUserInteractionFlag } from './workspace/useUserInteractionFlag';
import { BACKGROUND_LOCK_IDLE_RELEASE_MS } from 'src/store/workspace/workspaceConfig';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const Container = styled(Grid)({
  position: 'absolute',
  height: '100%',
  // The v7 Grid `container` no longer stretches to full width by default
  // (unlike the legacy Grid), so set it explicitly — the pedigree canvas reads
  // its parent's clientWidth/clientHeight and renders blank at zero width.
  width: '100%',
  top: 0,
  left: 0,
  overflow: 'hidden !important'
});

function PedigreeChartView({
  patient,
  familyId: familyIdProp,
  // This instance's workspace session, passed by the host. Read instead of
  // searching workspace state: a person tab and its family's pedigree tab share
  // a familyId, so a lookup by familyId cannot tell them apart.
  session,
  previewFamilyId,
  onPreviewError,
  // TGCLOUD-1669 Phase 7. Set when this instance is a PERSON tab rather than a
  // pedigree tab: the same family loads through the same chain, the canvas is
  // suppressed, and this member is selected on arrival. Threaded rather than
  // reimplemented because the whole persistence path lives below here:
  // PersonMenu's Save only mutates memberRecords and snacks "Saving…", and the
  // POST is PedigreeChart's debounced autosave, which alone reports the
  // outcome. A person region that rendered the sidebar without that chain
  // would discard every edit while telling the clinician it was saving it.
  personMemberId = null
}) {
  const t = useAutoTranslation();

  const isPreview = !!previewFamilyId;
  const isPersonTab = Boolean(personMemberId);
  const { content } = React.useContext(AppContext);
  const {
    pedigreeSelectedUsers: { selectedUser }
  } = content;

  const navigate = useNavigate();
  // In the multi-pedigree-tabs workspace the host mounts several instances at
  // once and passes each its own familyId; a lone chart (flag off) still reads
  // it from the route param. A read-only FHQ preview is never route-driven —
  // it names its shadow family explicitly, so it wins over both.
  const { _id: routeFamilyId } = useParams();
  const familyId = isPreview ? previewFamilyId : familyIdProp ?? routeFamilyId;
  const { dispatchUpdate } = React.useContext(AppContext);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    return () => {
      queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // In the multi-pedigree-tabs workspace, opening is VIEW-ONLY: the lock is not
  // taken on open (so N open tabs hold 0 locks) — it is acquired lazily on the
  // first edit of the active tab. A lone chart (no instance provider) keeps the
  // classic acquire-on-open behaviour.
  const { instanceId, isActive } = usePedigreeInstance();
  // ACTIONS only. `useWorkspace()` also hands back the state, and its value is
  // memoised on that state, so subscribing here re-rendered this whole chart on
  // every activation, lock badge and view-state patch in ANY tab. Measured
  // before the change: one tab switch committed all four open tabs.
  const ws = useWorkspaceActions();
  // `!isPreview` is load-bearing, not defensive: React context flows through the
  // MUI Dialog portal, so an FhqPreviewModal opened from inside the pedigree
  // subtree would inherit a non-null instanceId and start driving the HOST tab's
  // lock badge and tooltip with the SHADOW family's state. Gating here makes
  // every workspace side effect below inert in preview at a single point.
  const workspaceMode = instanceId != null && !isPreview;
  // Use the STABLE workspace callbacks (each useCallback([]) in the provider), not
  // the `ws` object — `ws` gets a new identity on every workspace-state change, so
  // depending on it in an effect that also dispatches is an infinite loop.
  const wsSetLock = ws?.setLock;
  const wsSetUnsavedSections = ws?.setUnsavedSections;
  const wsMarkEdited = ws?.markEdited;
  const wsSetProbandName = ws?.setProbandName;
  const wsMarkInaccessible = ws?.markInaccessible;

  const {
    lockState,
    isMine,
    holdsLock,
    lockResolved,
    requestStatus,
    requestLock,
    cancelRequestLock,
    forceAcquireLock,
    pendingRequest,
    respondToRequest,
    acquire,
    releaseLock
  } = usePedigreeLock(isPreview ? null : familyId, {
    // `!isPreview` as well as `!workspaceMode`: a preview is NOT workspace mode,
    // so autoAcquire would otherwise be true and a read-only dialog would take a
    // real edit lock on the shadow family and write an audit event saying so.
    autoAcquire: !workspaceMode && !isPreview,
    active: !workspaceMode || isActive
  });

  // In preview the chart is read-only, and that is enforced through isLocked —
  // it gates onSumbit, hides Bulk edit, and disables undo/redo, lasso and the
  // node menus. effLockState stays null so LockBanner early-returns instead of
  // claiming the family is "locked by another user". Do not collapse effIsMine /
  // effLockState back to isMine / lockState.
  const isLocked = isPreview ? true : !!(lockState && !isMine);
  const effIsMine = isPreview ? false : isMine;
  const effLockState = isPreview ? null : lockState;
  const { setSnack } = React.useContext(SnackContext);

  // Mark self-initiated releases (idle-release + manual release) so the lock-lost
  // watcher below doesn't mistake them for another user taking the lock.
  const selfReleaseRef = React.useRef(false);
  const doRelease = React.useCallback(() => {
    selfReleaseRef.current = true;
    releaseLock?.();
  }, [releaseLock]);

  // Second, independent gate on the edit signal (TGCLOUD-1665). PedigreeChart
  // already suppresses the engine's own layout writers by name, but that is a
  // blocklist: the next automatic writer someone adds and forgets to announce
  // would take the lock on open all over again — which is exactly how this bug
  // arose. A layout write that arrives before the user has touched this pedigree
  // cannot be theirs, whoever wrote it.
  const hasInteractedRef = useUserInteractionFlag(workspaceMode && isActive);

  // First edit of the active workspace tab: acquire the lock + auto-pin the tab so
  // the working context can't be silently recycled. Idempotent.
  //
  // `source` is 'layout' (node moves, ordering, arrange-lock — written by the
  // autosave effects, which the engine ALSO writes through) or 'data' (a clinical
  // save, which no automatic writer can produce). The interaction gate applies to
  // 'layout' only: a data save always follows user input, and gating it could
  // only ever fail in the dangerous direction — real record edits committed
  // without holding the lock.
  const onEdit = React.useCallback(
    (source) => {
      const isUserEdit = shouldSignalEdit({
        workspaceMode,
        isActive,
        source,
        hasInteracted: hasInteractedRef.current
      });
      if (!isUserEdit) return;
      acquire();
      if (instanceId) wsMarkEdited?.(instanceId);
    },
    [workspaceMode, isActive, acquire, instanceId, wsMarkEdited]
  );

  // Keep the tab's lock badge in sync with reality. Depends on the stable
  // setLock callback (not `ws`) + the actual lock inputs, so it only fires when
  // the badge value could change — SET_LOCK is also a no-op when unchanged.
  // A PERSON tab's badge reports the PERSON lock, not the family's. The two
  // are different facts: a clinician can hold a patient's record while the
  // family chart is free, or the reverse, and a strip that shows the family
  // lock on a patient tab is telling them about something they did not open
  // (TGCLOUD-1669 Phase 8).
  const [personLockState, setPersonLockState] = React.useState('none');
  const onPersonLockStateChange = React.useCallback(
    (next) => setPersonLockState(next),
    []
  );

  React.useEffect(() => {
    if (!workspaceMode || !instanceId || !wsSetLock) return;
    wsSetLock(
      instanceId,
      personMemberId
        ? personLockState
        : isMine
        ? 'self'
        : isLocked
        ? 'other'
        : 'none'
    );
  }, [
    workspaceMode,
    instanceId,
    wsSetLock,
    isMine,
    isLocked,
    personMemberId,
    personLockState
  ]);

  // The sidebar's unsaved sections, reported to this tab so the strip can ask
  // before a close discards them (TGCLOUD-1839). The sidebar is the only
  // writer: it reports on every change and [] as it unmounts, so the value
  // clears itself on Save, Discard and teardown.
  //
  // Gated on workspaceMode like every workspace write here: an FHQ preview
  // opened from inside this tab inherits its instanceId through the Dialog
  // portal, and its read-only sidebar reporting [] would otherwise clear THIS
  // tab's unsaved state out from under a clinician mid-edit. Stable for the
  // life of the mount (none of the three inputs change), so it costs the
  // memoised chart below nothing.
  const onUnsavedSectionsChange = React.useCallback(
    (sections) => {
      if (!workspaceMode || !instanceId || !wsSetUnsavedSections) return;
      wsSetUnsavedSections(instanceId, sections);
    },
    [workspaceMode, instanceId, wsSetUnsavedSections]
  );

  // Populate the tab tooltip with the proband name once the family has loaded, so
  // users can identify a family without knowing its G-number. SET_PROBAND_NAME is
  // a no-op when unchanged.
  const probandName =
    [selectedUser?.firstName, selectedUser?.lastName]
      .filter(Boolean)
      .join(' ') ||
    selectedUser?.name ||
    '';
  React.useEffect(() => {
    if (!workspaceMode || !familyId || !wsSetProbandName || !probandName)
      return;
    wsSetProbandName(familyId, probandName);
  }, [workspaceMode, familyId, wsSetProbandName, probandName]);

  // Background-tab lock release: if this tab holds the edit lock but has been the
  // INACTIVE tab for the idle window, release it so it never hogs the lock while a
  // clinician works elsewhere. The pedigree autosaves continuously, so by the idle
  // window everything is persisted. Re-activating (isActive) or losing the lock
  // (isMine) cancels the timer via the effect cleanup.
  React.useEffect(() => {
    // Gated on THIS MOUNT holding the lock, not on `isMine`. isMine is a
    // per-user fact: every mount of mine on a family reports it, so a
    // background PERSON tab used to arm this timer because my PEDIGREE tab
    // held the lock, and 120s later released it out from under the tab I was
    // editing in (TGCLOUD-1669 Phase 8).
    if (!workspaceMode || !holdsLock || isActive) return;
    const timer = setTimeout(doRelease, BACKGROUND_LOCK_IDLE_RELEASE_MS);
    return () => clearTimeout(timer);
  }, [workspaceMode, holdsLock, isActive, doRelease]);

  // Manual "Release lock" from the tab menu: the workspace bumps this session's
  // releaseNonce; release the lock if we hold it (works for a background tab).
  const releaseNonce = (workspaceMode && session?.releaseNonce) || 0;
  const prevReleaseNonceRef = React.useRef(releaseNonce);
  React.useEffect(() => {
    if (releaseNonce !== prevReleaseNonceRef.current) {
      prevReleaseNonceRef.current = releaseNonce;
      if (isMine) doRelease();
    }
  }, [releaseNonce, isMine, doRelease]);

  // Lock-lost: if we held the edit lock and it went away WITHOUT our releasing it
  // (another user force-took it, or it expired), tell the user cleanly — the chart
  // is now view-only and their unsaved work is protected by the autosave.
  const prevIsMineRef = React.useRef(isMine);
  React.useEffect(() => {
    const was = prevIsMineRef.current;
    prevIsMineRef.current = isMine;
    if (was && !isMine) {
      if (!selfReleaseRef.current && workspaceMode) {
        setSnack({
          status: true,
          severity: 'warning',
          msg: t(
            'Your edit lock on this pedigree was taken by another user — it is now view-only.'
          )
        });
      }
      selfReleaseRef.current = false;
    }
  }, [isMine, workspaceMode, setSnack]);

  const personLockHook = usePersonLocks(isPreview ? null : familyId, {
    active: !workspaceMode || isActive
  });

  // A session whose family could not be loaded (deleted, merged, or belonging to
  // another tenant) is flagged in the workspace. It must not be retried: the retry
  // is what turned one bad tab into a lockout, because every visit to the pedigree
  // route re-fetched it and the failure handler moved the router (TGCLOUD-1618).
  const sessionInaccessible = (workspaceMode && session?.inaccessible) || false;

  // Load ON ACTIVATION, not on mount — see shouldFetchFamily. Latched here (state,
  // not a ref, because the fetch effect has to re-run when it flips) so switching
  // away and back does not refetch.
  const [hasBeenActive, setHasBeenActive] = React.useState(
    !workspaceMode || isActive
  );
  React.useEffect(() => {
    if (isActive) setHasBeenActive(true);
  }, [isActive]);

  // Staleness guard for the family fetch (TGCLOUD-1804). This request had no
  // generation counter, so two in-flight loads resolved last-writer-wins and a
  // slow response for a family the user had already left could replace the one
  // they were looking at. `refreshViews` below already guards itself this way
  // for saved views — the family itself, which is the more consequential of
  // the two, did not.
  const familyReqRef = React.useRef(0);

  const getPedigreeDetails = () => {
    if (!familyId) return;
    const reqId = ++familyReqRef.current;
    const isCurrent = () => familyReqRef.current === reqId;
    // NOTE: deliberately does NOT drive the global LoadingContext / black BackdropLoader.
    // The Canvas renders its own "Loading family…" overlay while the worker computes the
    // layout; showing the global black spinner too produced a double spinner on open (the
    // backdrop's fade-out overlapped the Canvas overlay). This view's loading UX is the
    // Canvas overlay alone.
    axios
      // ?auditView=1 marks this as a genuine record OPEN (a Tier-2 access event),
      // distinct from the post-edit data reloads that hit the same endpoint. A
      // preview is not a record open, so it is omitted there.
      .get(
        `${APICONSTANTS.getPedigreeDetails.replace(':familyId', familyId)}${
          // A person tab loads the same family as its pedigree tab would, so
          // sending the flag from both would write two Tier-2 "record opened"
          // events keyed on the proband for one clinician action. Decided with
          // the CTO (TGCLOUD-1669 Phase 7): the person tab sends no flag.
          isPreview || isPersonTab ? '' : '?auditView=1'
        }`
      )
      .then((resp) => {
        if (!isCurrent()) return;
        const { proband, families, perf } = resp.data;

        const value = {
          family: families,
          selectedUser: proband,
          perf
        };

        dispatchUpdate({ type: EVENTS.SET_PEDIGREE_SELECTED_USER, value });
      })
      .catch(() => {
        // A superseded request must not mark the CURRENT family inaccessible
        // or move the router away from it.
        if (!isCurrent()) return;
        // The rules live in familyLoadPolicy so they are pinned by tests rather
        // than only reachable by mounting this whole view. The tab strip already
        // renders "(unavailable)" off `inaccessible` and offers a close button, so
        // flagging is all this needs to do to explain itself.
        const effects = familyLoadFailureEffects({
          isPreview,
          workspaceMode,
          isActive,
          sessionKind: isPersonTab ? 'person' : 'pedigree'
        });
        if (effects.notifyPreview) onPreviewError?.();
        if (effects.markInaccessible) wsMarkInaccessible?.(familyId);
        if (effects.navigateToList) navigate(CONSTANTS.pedigreeToolPath);
      });
  };

  React.useEffect(() => {
    if (!shouldFetchFamily({ familyId, sessionInaccessible, hasBeenActive })) {
      return;
    }
    getPedigreeDetails();
  }, [familyId, sessionInaccessible, hasBeenActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const pedigree = React.useMemo(() => {
    return (
      <>
        {selectedUser?._id && (
          <PedigreeBox
            personMemberId={personMemberId}
            onPersonLockStateChange={onPersonLockStateChange}
            onUnsavedSectionsChange={onUnsavedSectionsChange}
            // The persisted view state, read ONCE at mount. Passed rather than
            // looked up, so ChartBox does not have to subscribe to workspace
            // state for a value it reads a single time.
            initialViewState={session?.viewState}
            patient={patient}
            isLocked={isLocked}
            // Whether the family lock has actually been READ (TGCLOUD-1813).
            // isLocked above is false until usePedigreeLock's first
            // acquire/GET succeeds — and stays false after a FAILED one,
            // which this flag therefore does not count as read — so the
            // chart's automatic placeholder save waits on this rather than
            // deciding on a not-yet-known lock. A
            // preview takes no lock at all and is read-only through
            // isLocked=true, so its answer is known from the start.
            lockResolved={isPreview ? true : lockResolved}
            isMine={effIsMine}
            lockState={effLockState}
            requestStatus={isPreview ? null : requestStatus}
            onRequestLock={isPreview ? () => {} : requestLock}
            onCancelRequest={isPreview ? () => {} : cancelRequestLock}
            onForceAcquireLock={isPreview ? () => {} : forceAcquireLock}
            pendingRequest={isPreview ? null : pendingRequest}
            onRespondToRequest={isPreview ? () => {} : respondToRequest}
            personLockHook={personLockHook}
            onEdit={isPreview ? undefined : onEdit}
            preview={isPreview}
          />
        )}
      </>
    );
  }, [
    patient,
    selectedUser,
    isLocked,
    lockResolved,
    effIsMine,
    effLockState,
    requestStatus,
    pendingRequest,
    personLockHook,
    onEdit,
    isPreview,
    onUnsavedSectionsChange
  ]);

  return <Container container>{pedigree}</Container>;
}

export default PedigreeChartView;
